import {
  Dispatch,
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { TPaginationParams } from '../../../../apis/__shared/shared.interfaces';
import ERROR_MESSAGE from '../../../../utils/ErrorMessages';
import { useFinancialGrantsHook } from '../hooks/useFinancialGrantsHook';
import { TFinancialGrant } from '../../../../apis/finance/financialGrants.interfaces';

type TFinancialGrantsAppState = {
  errorMessage?: string;
  isLoading: boolean;
  requestParams: TPaginationParams;
  financialGrantsList: TFinancialGrant[];
  isManageFinancialGrantItemOpen: boolean;
  isFinancialGrantItemLoading: boolean;
  isFinancialGrantItemError: boolean;
  financialGrantItemErrorMessage: string;
  selectedFinancialGrantItem: TFinancialGrant | null;
  selectedFinancialGrantItemId: string | null;
  isCreateMode: boolean;
  isEditMode: boolean;
};

enum ACTION_TYPE {
  SET_REQUEST_PARAMS = 'SET_REQUEST_PARAMS',
  UPDATE_FINANCIAL_GRANTS_LIST = 'UPDATE_FINANCIAL_GRANTS_LIST',
  FETCHING_DATA = 'FETCHING_DATA',
  FETCH_DATA_FAILURE = 'FETCH_DATA_FAILURE',
  SET_IS_MANAGE_FINANCIAL_GRANT_ITEM_OPEN = 'SET_IS_MANAGE_FINANCIAL_GRANT_ITEM_OPEN',
  SET_SELECTED_FINANCIAL_GRANT_ITEM = 'SET_SELECTED_FINANCIAL_GRANT_ITEM',
  SET_SELECTED_FINANCIAL_GRANT_ITEM_ID = 'SET_SELECTED_FINANCIAL_GRANT_ITEM_ID',
  SET_IS_CREATE_MODE = 'SET_IS_CREATE_MODE',
  SET_IS_EDIT_MODE = 'SET_IS_EDIT_MODE',
  SET_IS_FINANCIAL_GRANT_ITEM_LOADING = 'SET_IS_FINANCIAL_GRANT_ITEM_LOADING',
  SET_IS_FINANCIAL_GRANT_ITEM_ERROR = 'SET_IS_FINANCIAL_GRANT_ITEM_ERROR',
  SET_FINANCIAL_GRANT_ITEM_ERROR_MESSAGE = 'SET_FINANCIAL_GRANT_ITEM_ERROR_MESSAGE',
}

type Action =
  | { type: ACTION_TYPE.SET_REQUEST_PARAMS; payload: TPaginationParams }
  | {
      type: ACTION_TYPE.UPDATE_FINANCIAL_GRANTS_LIST;
      payload: TFinancialGrant[];
    }
  | { type: ACTION_TYPE.FETCHING_DATA; payload: boolean }
  | { type: ACTION_TYPE.FETCH_DATA_FAILURE; payload: string }
  | {
      type: ACTION_TYPE.SET_IS_MANAGE_FINANCIAL_GRANT_ITEM_OPEN;
      payload: boolean;
    }
  | { type: ACTION_TYPE.SET_IS_FINANCIAL_GRANT_ITEM_LOADING; payload: boolean }
  | { type: ACTION_TYPE.SET_IS_FINANCIAL_GRANT_ITEM_ERROR; payload: boolean }
  | {
      type: ACTION_TYPE.SET_FINANCIAL_GRANT_ITEM_ERROR_MESSAGE;
      payload: string;
    }
  | {
      type: ACTION_TYPE.SET_SELECTED_FINANCIAL_GRANT_ITEM;
      payload: TFinancialGrant | null;
    }
  | {
      type: ACTION_TYPE.SET_SELECTED_FINANCIAL_GRANT_ITEM_ID;
      payload: string | null;
    }
  | { type: ACTION_TYPE.SET_IS_CREATE_MODE; payload: boolean }
  | { type: ACTION_TYPE.SET_IS_EDIT_MODE; payload: boolean };

const initialFinancialGrantsAppState: TFinancialGrantsAppState = {
  financialGrantsList: [],
  errorMessage: undefined,
  isLoading: false,
  requestParams: {
    page: 1,
    limit: 10,
  },
  isManageFinancialGrantItemOpen: false,
  isFinancialGrantItemLoading: false,
  isFinancialGrantItemError: false,
  financialGrantItemErrorMessage: '',
  selectedFinancialGrantItem: null,
  selectedFinancialGrantItemId: null,
  isCreateMode: false,
  isEditMode: false,
};

type TState = {
  financialGrantsAppState: TFinancialGrantsAppState;
  financialGrantsList: TFinancialGrant[];
};

const FinancialGrantsContext = createContext<TState | undefined>(undefined);
const FinancialGrantsDispatchContext = createContext<
  Dispatch<Action> | undefined
>(undefined);

const FinancialGrantsReducer = (
  state: TFinancialGrantsAppState,
  action: Action
): TFinancialGrantsAppState => {
  switch (action.type) {
    case ACTION_TYPE.SET_REQUEST_PARAMS:
      return {
        ...state,
        requestParams: { ...state.requestParams, ...action.payload },
      };
    case ACTION_TYPE.UPDATE_FINANCIAL_GRANTS_LIST:
      return { ...state, financialGrantsList: action.payload };
    case ACTION_TYPE.FETCHING_DATA:
      return { ...state, isLoading: action.payload, errorMessage: '' };
    case ACTION_TYPE.FETCH_DATA_FAILURE:
      return { ...state, isLoading: false, errorMessage: action.payload };
    case ACTION_TYPE.SET_IS_MANAGE_FINANCIAL_GRANT_ITEM_OPEN:
      return { ...state, isManageFinancialGrantItemOpen: action.payload };
    case ACTION_TYPE.SET_IS_FINANCIAL_GRANT_ITEM_LOADING:
      return { ...state, isFinancialGrantItemLoading: action.payload };
    case ACTION_TYPE.SET_IS_FINANCIAL_GRANT_ITEM_ERROR:
      return { ...state, isFinancialGrantItemError: action.payload };
    case ACTION_TYPE.SET_FINANCIAL_GRANT_ITEM_ERROR_MESSAGE:
      return { ...state, financialGrantItemErrorMessage: action.payload };
    case ACTION_TYPE.SET_SELECTED_FINANCIAL_GRANT_ITEM:
      return { ...state, selectedFinancialGrantItem: action.payload };
    case ACTION_TYPE.SET_SELECTED_FINANCIAL_GRANT_ITEM_ID:
      return { ...state, selectedFinancialGrantItemId: action.payload };
    case ACTION_TYPE.SET_IS_CREATE_MODE:
      return { ...state, isCreateMode: action.payload };
    case ACTION_TYPE.SET_IS_EDIT_MODE:
      return { ...state, isEditMode: action.payload };
    default:
      throw new Error('Unhandled action type');
  }
};

export const FinancialGrantsProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [financialGrantsAppState, dispatch] = useReducer(
    FinancialGrantsReducer,
    initialFinancialGrantsAppState
  );

  const {
    data: financialGrantsResponse,
    isLoading: financialGrantsResponseLoading,
    isError: financialGrantsResponseLoadingError,
    isSuccess: financialGrantsResponseLoadingDataSuccess,
    error: financialGrantsResponseDataError,
  } = useFinancialGrantsHook(financialGrantsAppState.requestParams);

  const finalFinancialGrantsList = useMemo(() => {
    if (financialGrantsResponseLoading) return [];
    return financialGrantsResponse?.data ?? [];
  }, [financialGrantsResponse, financialGrantsResponseLoading]);

  useEffect(() => {
    if (financialGrantsResponseLoading) {
      dispatch({ type: ACTION_TYPE.FETCHING_DATA, payload: true });
    } else if (financialGrantsResponseLoadingDataSuccess) {
      dispatch({ type: ACTION_TYPE.FETCHING_DATA, payload: false });
    } else if (financialGrantsResponseLoadingError) {
      dispatch({
        type: ACTION_TYPE.FETCH_DATA_FAILURE,
        payload:
          financialGrantsResponseDataError?.response?.data.message ??
          ERROR_MESSAGE.GET_FINANCE_LIST_FAILURE,
      });
    }

    return () => {
      dispatch({ type: ACTION_TYPE.FETCHING_DATA, payload: false });
    };
  }, [
    financialGrantsResponseLoading,
    financialGrantsResponseLoadingDataSuccess,
    financialGrantsResponseLoadingError,
    financialGrantsResponseDataError,
  ]);

  useEffect(() => {
    if (financialGrantsResponse?.data) {
      dispatch({
        type: ACTION_TYPE.UPDATE_FINANCIAL_GRANTS_LIST,
        payload: financialGrantsResponse.data,
      });
    }
  }, [financialGrantsResponse?.data]);

  const currentState = useMemo<TState>(
    () => ({
      financialGrantsAppState,
      financialGrantsList: finalFinancialGrantsList,
    }),
    [financialGrantsAppState, finalFinancialGrantsList]
  );

  return (
    <FinancialGrantsDispatchContext.Provider value={dispatch}>
      <FinancialGrantsContext.Provider value={currentState}>
        {children}
      </FinancialGrantsContext.Provider>
    </FinancialGrantsDispatchContext.Provider>
  );
};

export const useFinancialGrantsState = () => {
  const state = useContext(FinancialGrantsContext);
  if (state === undefined) {
    throw new Error(
      'useFinancialGrantsState must be used within a FinancialGrantsProvider'
    );
  }
  return state;
};

export const useFinancialGrantsActions = () => {
  const dispatch = useContext(FinancialGrantsDispatchContext);
  if (!dispatch) {
    throw new Error(
      'useFinancialGrantsActions dispatch must be used within a FinancialGrantsProvider'
    );
  }

  const updateSearchParams = (params: TPaginationParams) => {
    dispatch({ type: ACTION_TYPE.SET_REQUEST_PARAMS, payload: params });
  };

  const updateFinancialGrantsList = (list: TFinancialGrant[]) => {
    dispatch({
      type: ACTION_TYPE.UPDATE_FINANCIAL_GRANTS_LIST,
      payload: list,
    });
  };

  const setIsManageFinancialGrantItemOpen = (isOpen: boolean) => {
    dispatch({
      type: ACTION_TYPE.SET_IS_MANAGE_FINANCIAL_GRANT_ITEM_OPEN,
      payload: isOpen,
    });
  };

  const setSelectedFinancialGrantItem = (item: TFinancialGrant | null) => {
    dispatch({
      type: ACTION_TYPE.SET_SELECTED_FINANCIAL_GRANT_ITEM,
      payload: item,
    });
  };

  const setIsFinancialGrantItemLoading = (isLoading: boolean) => {
    dispatch({
      type: ACTION_TYPE.SET_IS_FINANCIAL_GRANT_ITEM_LOADING,
      payload: isLoading,
    });
  };

  const setIsFinancialGrantItemError = (isError: boolean) => {
    dispatch({
      type: ACTION_TYPE.SET_IS_FINANCIAL_GRANT_ITEM_ERROR,
      payload: isError,
    });
  };

  const setFinancialGrantItemErrorMessage = (errorMessage: string) => {
    dispatch({
      type: ACTION_TYPE.SET_FINANCIAL_GRANT_ITEM_ERROR_MESSAGE,
      payload: errorMessage,
    });
  };

  const setSelectedFinancialGrantItemId = (itemId: string | null) => {
    dispatch({
      type: ACTION_TYPE.SET_SELECTED_FINANCIAL_GRANT_ITEM_ID,
      payload: itemId,
    });
  };

  const setIsCreateMode = (isCreateMode: boolean) => {
    dispatch({ type: ACTION_TYPE.SET_IS_CREATE_MODE, payload: isCreateMode });
  };

  const setIsEditMode = (isEditMode: boolean) => {
    dispatch({ type: ACTION_TYPE.SET_IS_EDIT_MODE, payload: isEditMode });
  };

  return {
    updateSearchParams,
    updateFinancialGrantsList,
    setIsManageFinancialGrantItemOpen,
    setSelectedFinancialGrantItem,
    setIsFinancialGrantItemLoading,
    setIsFinancialGrantItemError,
    setFinancialGrantItemErrorMessage,
    setSelectedFinancialGrantItemId,
    setIsCreateMode,
    setIsEditMode,
  };
};
